import React, {useMemo} from 'react'
import {DesignSystemColorDefinition} from '@api/shared/constants/colors'

export type FontName =
  // Headlines
  | 'Headlines/H1'
  | 'Headlines/H2'
  | 'Headlines/H3'
  // Subtitle
  | 'Subtitle/Md'
  | 'Subtitle/Sm'
  | 'Subtitle/Xs [Caps]'
  // Body
  | 'Body/Md'
  | 'Body/Sm'
  | 'Body/Xs'
  // Caption
  | 'Caption/Chat Name'
  | 'Caption/Sticker'
  | 'Caption/Counter'
  | 'Caption/Badge'
  // Button
  | 'Button/Md'
  | 'Button/Sm'
export interface FontProps {
  name: FontName
  color: DesignSystemColorDefinition | string
  as?: React.ElementType
  children: React.ReactNode
  style?: React.CSSProperties
}

const nameToClassNameMap: Record<FontName, string> = {
  // Headlines
  'Headlines/H1': 'headline-h1',
  'Headlines/H2': 'h1-semibold',
  'Headlines/H3': 'h2-semibold',
  // Subtitle
  'Subtitle/Md': 'text-sm-semibold',
  'Subtitle/Sm': 'text-sm-regular',
  'Subtitle/Xs [Caps]': 'text-subtitle-xs uppercase',
  // Body
  'Body/Md': 'body-long-regular',
  'Body/Sm': 'text-body',
  'Body/Xs': 'micro-regular',
  // Caption
  'Caption/Chat Name': 'micro-semibold',
  'Caption/Sticker': 'micro-regular',
  'Caption/Counter': 'text-xxs-regular',
  'Caption/Badge': 'text-small-label',
  // Button
  'Button/Md': 'table-content-semibold',
  'Button/Sm': 'h4-menu-title'
}

const resolveColor = (color: DesignSystemColorDefinition | string) => {
  if (typeof color === 'string') {
    return color
  } else if (color.cssVariable) {
    return `var(${color.cssVariable})`
  }
  return `inherit`
}

export const SharedFont = (props: FontProps) => {
  const {name, as, color, style} = props
  const className = nameToClassNameMap[name] || ''
  const Component = as || 'span'
  const finalStyle = useMemo(
    () => ({
      ...style,
      ...(color
        ? {
            color: resolveColor(color)
          }
        : undefined)
    }),
    [color]
  )
  return (
    <Component className={className} style={finalStyle}>
      {props.children}
    </Component>
  )
}

SharedFont.names = {
  headlinesH1: 'Headlines/H1',
  headlinesH2: 'Headlines/H2',
  headlinesH3: 'Headlines/H3',
  subtitleMd: 'Subtitle/Md',
  subtitleSm: 'Subtitle/Sm',
  subtitleXsCaps: 'Subtitle/Xs [Caps]',
  bodyMd: 'Body/Md',
  bodySm: 'Body/Sm',
  bodyXs: 'Body/Xs',
  captionChatName: 'Caption/Chat Name',
  captionSticker: 'Caption/Sticker',
  captionCounter: 'Caption/Counter',
  captionBadge: 'Caption/Badge',
  buttonMd: 'Button/Md',
  buttonSm: 'Button/Sm'
}
